import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import * as yup from "yup";
import useYupValidationResolver from "src/hooks/useYupValidationResolver";
import dayjs from "dayjs";

import * as MatStyled from "@mui/material/styles";
import {
  Grid,
  FormControl,
  FormLabel,
  FormControlLabel,
  FormHelperText,
  RadioGroup,
  Radio,
  Box,
  Stack,
  Typography,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import AddIcon from "@mui/icons-material/Add";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import font from "src/styles/font";
import { Black, DarkGrey, Primary, White01 } from "src/styles/color";
import { hexToRgba } from "src/common/color";

import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";
import LabelSection from "src/components/core/LabelSection";
import TextEditor from "src/components/core/TextEditor";
import Checkbox from "src/components/core/Checkbox";
import Loading from "src/components/ui/Loading";
import { useDialog } from "src/context/DialogContext";
import { convertPrice } from "src/common";
import { findOnePromoCode } from "src/api/promoCode";

import AddProductDialog from "./AddProductDialog";
import SuggestConditionDialog from "./SuggestConditionDialog";
import PreviewDialog from "./PreviewDialog";

const Container = styled.div`
  width: 100%;
  max-width: 1240px;
  position: relative;
  margin: 0 auto;
  padding: 32px;
  padding-bottom: 100px;
`;

const BoxWrapper = styled.div`
  width: 100%;
  height: auto;
  padding: 32px;
  border-radius: 32px;
  background-color: #ffffff;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  // display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 32px;
`;

const SpanLabel = styled.div`
  font-size: ${font.size.lg};
  color: var(--secondary-color);
  font-weight: 400;
  margin-bottom: 24px;

  span {
    font-size: ${font.size.lg};
    font-weight: 400;
    color: var(--primary-color);
  }
`;

const ImageNotFound = styled.div`
  width: 120px;
  height: 120px;
  background-image: url("https://xsurface-images.s3.ap-southeast-1.amazonaws.com/not+found.svg");
  background-repeat: no-repeat;
  background-size: 100%;
`;

const ImageNoData = styled.image`
  width: 120px;
  height: 120px;
  background-image: url("https://xsurface-images.s3.ap-southeast-1.amazonaws.com/no+data.svg");
  background-repeat: no-repeat;
  background-size: 100%;
`;

const TextNoData = styled.pre`
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
`;

const ImageEmpty = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: #f2f1f3;
  border-radius: 16px;
  object-fit: cover;
  object-position: center;
  cursor: pointer;
  vertical-align: initial;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
  }

  &.active {
    border: 2px solid #707070;
  }
`;

const ImgDisplay = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  object-position: center;
  border-radius: 16px;
  flex-shrink: 0;
`;

const ButtonGroup = styled(Stack)`
  /* width: 100%;
  max-width: 1240px; */

  width: 100%;
  height: 100px;
  background-color: var(--white-color);
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 15;
  /* margin-top: 24px; */
  padding: 24px 40px;
  border-radius: 24px 24px 0 0;
  box-shadow: 0px -2px 10px 0px rgba(0, 0, 0, 0.1);

  .MuiButton-root {
    padding: 12px 24px;
    font-size: ${font.size.sm};
  }
`;

const WrapperDataGrid = styled.div`
  height: 600px;
  width: 100%;
  .MuiDataGrid-root {
    border: 1px solid #ffffff;
  }
  .MuiDataGrid-columnHeaders {
    background-color: #f5f5f5;
  }
  .MuiDataGrid-root .MuiDataGrid-columnSeparator--sideRight {
    color: #f5f5f5;
  }
  .MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within {
    // outline: solid #f5f5f5 1px;
    outline: none;
  }
  .MuiDataGrid-root .MuiDataGrid-cell:focus {
    outline: none;
    border-bottom: solid #f5f5f5 1px 0 1px 0;
  }
  .MuiDataGrid-root .MuiDataGrid-row.Mui-selected {
    background-color: #ffffff;
  }
  .MuiCheckbox-root.Mui-checked {
    color: var(--primary-color);
  }
`;

const drawerWidth = 260;

const Main = MatStyled.styled("main", {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  position: "relative",
  flexGrow: 1,
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginTop: "75px",
  width: "100%",
  height: "calc(100vh - 75px)",
  overflow: "hidden",
  overflowY: "scroll",
  scrollBehavior: "smooth",
  background: "#fff",
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
    position: "relative",
  }),
}));

const validationSchema = yup.object().shape({
  promo_name: yup.string().required(),
  promo_code: yup.string().required(),
  promo_type: yup.string().oneOf(["product", "bill", "delivery"]).required(),
  promo_condition: yup.object({
    promo_condition_user: yup
      .string()
      .oneOf(["all", "new", "repeat"])
      .required(),
    promo_condition_repeat: yup.boolean().required(),
  }),
  promo_product_group: yup
    .string()
    .oneOf(["all", "ready", "pre-order"])
    .required(),
  promo_pay_type: yup
    .string()
    .oneOf(["all", "promptpay", "credit-card", "installment"])
    .required(),
  promo_discount: yup.object({
    promo_discount_type: yup.string().oneOf(["percent", "amount"]).required(),
    promo_discount_value: yup
      .number()
      .nullable()
      .test({
        name: "requires",
        message: (value) => {
          if (value.value < 0) {
            return `promo discount is must more than 0`;
          }
          // return `${value.path} is a required field`;
          return ``;
        },
        test: (_, context) => {
          if (
            context?.parent.promo_discount_type === "amount" &&
            (context?.originalValue === null || context.originalValue < 0)
          ) {
            return false;
          }
          return true;
        },
      }),
    promo_discount_percent: yup
      .number()
      .nullable()
      .test({
        name: "requires",
        message: (value) => {
          if (value.value <= 0) {
            return `promo discount percent is must more than 0`;
          } else if (value.value > 0) {
            return `promo discount percent is must less than 100`;
          }
          // return `${value.path} is a required field`;
          return ``;
        },
        test: (_, context) => {
          if (
            context?.parent.promo_discount_type === "percent" &&
            (context?.originalValue === null ||
              context.originalValue <= 0 ||
              context.originalValue > 100)
          ) {
            return false;
          }
          return true;
        },
      }),
    promo_discount_minimum_spend: yup.number().min(0).required(),
    promo_discount_maximum: yup
      .number()
      .nullable()
      .test({
        name: "requires",
        message: (value) => {
          return `${value.path} is a required field`;
        },
        test: (_, context) => {
          if (
            context?.parent.promo_discount_type === "percent" &&
            (context?.originalValue === null || context.originalValue <= 0)
          ) {
            return false;
          }
          return true;
        },
      }),
  }),
  promo_responsible: yup.object({
    promo_responsible_by: yup
      .string()
      .oneOf(["half", "xsurface", "seller"])
      .required(),
    promo_xsurface_percent: yup
      .number()
      .nullable()
      .test({
        name: "requires",
        message: (value) => {
          return `${value.path} is a required field`;
        },
        test: (_, context) => {
          if (
            context?.parent.promo_responsible_by === "half" &&
            (context?.originalValue === null || context.originalValue <= 0)
          ) {
            return false;
          }
          return true;
        },
      }),
    promo_seller_percent: yup
      .number()
      .nullable()
      .test({
        name: "requires",
        message: (value) => {
          return `${value.path} is a required field`;
        },
        test: (_, context) => {
          if (
            context?.parent.promo_responsible_by === "half" &&
            (context?.originalValue === null || context.originalValue <= 0)
          ) {
            return false;
          }
          return true;
        },
      }),
  }),
  promo_start_time: yup
    .date()
    .required("Please select promo code start date.")
    .test({
      name: "required",
      message: "Promo code start date should be before promo code expire date.",
      test: (_, context) => {
        if (
          !!context?.parent?.promo_expire_time &&
          dayjs(context?.originalValue).isAfter(
            dayjs(context?.parent?.promo_expire_time)
          )
        ) {
          return false;
        }
        return true;
      },
    }),
  promo_expire_time: yup
    .date()
    .required("Please select promo code expiry date.")
    .test({
      name: "required",
      message: "Promo code expire date should be after promo code start date.",
      test: (_, context) => {
        if (
          !!context?.parent?.promo_start_time &&
          dayjs(context?.originalValue).isBefore(
            dayjs(context?.parent?.promo_start_time)
          )
        ) {
          return false;
        }
        return true;
      },
    }),
  amount: yup
    .number()
    .nullable()
    .when("$amount_unlimit", ([amount_unlimit], schema) =>
      amount_unlimit ? schema : schema.required()
    ),
  promo_title: yup.string().required(),
  promo_description: yup.string().required(),
  product_choice_promocodes: yup
    .array()
    .of(
      yup.object({
        product_choice_id: yup.object(),
      })
    )
    .test({
      name: "required",
      message: "Product list should not empty product field.",
      test: (array, context) => {
        if (array.length > 1) {
          return array.every(
            ({ _id: product_choice_id }) => !!product_choice_id
          );
        }
        return true;
      },
    })
    .min(1)
    .required(),
  note: yup.string(),
});

function NoRowsOverlay() {
  return (
    <Stack height="100%" alignItems="center" justifyContent="center">
      <ImageNoData></ImageNoData>
      <TextNoData>No Data</TextNoData>
    </Stack>
  );
}

function NoResultsOverlay() {
  return (
    <Stack height="100%" alignItems="center" justifyContent="center">
      <ImageNotFound />
      <TextNoData>Not Found</TextNoData>
    </Stack>
  );
}

const CreatePromoCode = ({ open }) => {
  let { codeId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const copyId = searchParams.get("copyId");
  const { openDialog, closeDialog, closeAllDialogs } = useDialog();

  const [isLoading, setIsLoading] = useState(false);

  const [isUnlimit, setIsUnlimit] = useState(false);
  const defaultValues = {
    _id: null,
    promo_name: "",
    promo_code: "",
    promo_type: "product", // "product", "bill", "delivery"
    promo_condition: {
      promo_condition_user: "new", // "all", "new", "repeat"
      promo_condition_repeat: true,
    },
    promo_product_group: "ready", //"ready", "pre-order", "all"
    promo_pay_type: "promptpay", // "promptpay", "credit-card", "installment", "all"
    promo_discount: {
      promo_discount_type: "", // "percent", "amount"
      promo_discount_value: null,
      promo_discount_percent: null,
      promo_discount_minimum_spend: null,
      promo_discount_maximum: null,
    },
    promo_responsible: {
      promo_responsible_by: "xsurface", // "xsurface", "seller", "split"
      promo_xsurface_percent: null,
      promo_seller_percent: null,
    },
    promo_start_time: null,
    promo_expire_time: null,
    amount: null,
    used_amount: 0,
    promo_title: "",
    promo_description: "",
    product_choice_promocodes: [],
    note: "",
  };
  const [formData, setFormData] = useState(defaultValues);
  const [editorLoaded, setEditorLoaded] = useState(false);

  let resolver = useYupValidationResolver(validationSchema, {
    amount_unlimit: isUnlimit,
  });

  const {
    formState: { errors: formErrors, isValid },
    control: formControl,
    watch,
    trigger,
    getValues,
    setValue: setFormValue,
    reset,
  } = useForm({
    resolver,
    defaultValues,
  });

  // console.log("formData:", formData);
  // console.log("formState -> formErrors", formErrors);
  // console.log("formState -> isValid", isValid);
  // console.log("Object.keys(formErrors)", Object.keys(formErrors));

  const { remove: removeElement } = useFieldArray({
    control: formControl,
    name: "product_choice_promocodes",
  });

  const fetchCodeData = async () => {
    setIsLoading(true);
    try {
      // console.log('--- codeId ---', codeId, '=====', copyId);
      let id = codeId || copyId;

      const data = await findOnePromoCode(id);

      if (!!data) {
        reset({
          ...data,
          ...(!!copyId
            ? {
                _id: null,
                promo_name: null,
                promo_code: null,
                promo_start_time: null,
                promo_expire_time: null,
              }
            : {
                _id: data._id,
                promo_name: data.promo_name,
                promo_code: data.promo_code,
                promo_start_time: data.promo_start_time,
                promo_expire_time: data.promo_expire_time,
              }),
        });
        setIsUnlimit(!!data?.amount ? false : true);
      }
    } catch (err) {
      console.error("err", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!!!codeId && !!!copyId) return;

    fetchCodeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeId]);

  useEffect(() => {
    setEditorLoaded(true);
  }, []);

  const handleChange = async (event, replaceName, replaceValue) => {
    // console.log('---- e ---', event, '===', replaceName, '--- replaceValue ---', replaceValue)
    let name =
      typeof replaceName !== "undefined" ? replaceName : event.target.name;
    let value =
      typeof replaceValue !== "undefined" ? replaceValue : event.target.value;

    let path = name.split(".");
    let [key, ...rest] = path;

    let editingKey = key;
    let updated;

    if (key === "promo_start_time") {
      updated = value.$d;
    } else if (key === "promo_expire_time") {
      let dateFormat = dayjs(value.$d).format("DD/MM/YYYY, 23:59:59");
      updated = dayjs(dateFormat, "DD/MM/YYYY, HH:mm:ss").$d;
    } else if (key === "promo_condition") {
      let [objKey] = rest;
      let editingRow = getValues(`promo_condition`);

      // if (objKey === 'promo_condition_repeat') {
      //   if (value) {
      //     updated = null;
      //   } else {
      //     updated = "";
      //   }
      // } else {
      //   updated = value;
      // }
      updated = {
        ...editingRow,
        [objKey]: value,
      };
    } else if (key === "amount") {
      let [objKey] = rest;
      if (objKey === "is_unlimit") {
        if (value) {
          updated = null;
        } else {
          updated = "";
        }
      } else {
        updated = value;
      }
    } else if (key === "promo_discount") {
      let [objKey] = rest;
      let editingRow = getValues(`promo_discount`);

      if (objKey === "promo_discount_type") {
        updated = {
          ...editingRow,
          promo_discount_type: value,
          promo_discount_value: null,
          promo_discount_percent: null,
          promo_discount_minimum_spend: null,
          promo_discount_maximum: null,
        };
      } else {
        updated = {
          ...editingRow,
          [objKey]: value,
        };
      }
    } else if (key === "promo_responsible") {
      let [objKey] = rest;
      let editingRow = getValues(`promo_responsible`);

      // console.log('objKey', objKey, '----', value);

      if (objKey === "promo_responsible_by") {
        updated = {
          ...editingRow,
          promo_responsible_by: value,
          promo_xsurface_percent: null,
          promo_seller_percent: null,
        };
      } else {
        updated = {
          ...editingRow,
          [objKey]: value,
        };
      }
    } else {
      updated = value;
    }
    // else if (key === "product_choice_promocodes") {
    //   updated = value;
    // }

    setFormValue(editingKey, updated);
  };

  /**
   * @notice after form value change, then set value to formData state
   */
  useEffect(() => {
    const subscription = watch((values, { name, type }) => {
      setFormData(values);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  /**
   * @notice trigger validation when formData updated
   */
  useEffect(() => {
    trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const columns = [
    {
      field: "product_choice_promocode",
      headerName: "ชื่อสินค้า/รหัส",
      flex: 4,
      sortable: false,
      align: "left",
      userRoles: [1],
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              py: "10px",
            }}
          >
            {!!params?.row?.group_image?.product_display_image === false ? (
              <ImageEmpty>
                <InsertPhotoIcon
                  sx={{
                    color: "rgba(0, 0, 0, 0.54)",
                    cursor: "pointer",
                  }}
                />
              </ImageEmpty>
            ) : (
              <ImgDisplay
                src={
                  params?.row?.group_image?.product_display_image
                  // ? params?.row?.group_image?.product_display_image.replace(
                  //     ".com",
                  //     ".com/300x300"
                  //   )
                  // : ""
                }
                alt=""
              />
            )}
            <Stack
              gap={1}
              sx={{
                ml: 2,
                "& .MuiTypography-root": {
                  fontSize: font.size.sm,
                },
                "& span.MuiTypography-root": {
                  color: DarkGrey,
                },
              }}
            >
              <Typography component="p">
                {params?.row?.product?.item_name}
              </Typography>
              <Typography component="span">
                {params?.row?.product?.item_code}
              </Typography>
              {!!params?.row?.name && (
                <Typography component="span">{params?.row?.name}</Typography>
              )}
            </Stack>
          </Box>
        );
      },
    },
    {
      field: "company_name",
      headerName: "แบรนด์",
      flex: 2,
      sortable: false,
      headerAlign: "left",
      align: "left",
      userRoles: [1],
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            component="p"
            sx={{
              "&.MuiTypography-root": {
                fontSize: font.size.sm,
              },
            }}
          >
            {params?.row?.product?.brand?.name}
          </Typography>
        </Box>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Updated Date",
      flex: 3,
      sortable: true,
      headerAlign: "center",
      align: "center",
      userRoles: [1],
      renderCell: (params) =>
        dayjs(params?.row?.product_choice_deal?.updatedAt).format("DD-MM-YYYY"),
    },
    {
      field: "price",
      headerName: "Price",
      flex: 2,
      sortable: false,
      headerAlign: "center",
      align: "center",
      userRoles: [1],
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            component="p"
            sx={{
              "&.MuiTypography-root": {
                fontSize: font.size.sm,
              },
            }}
          >
            {`${convertPrice(
              params?.row?.group_price?.is_start_price === true
                ? params?.row?.group_price?.start_price
                : params?.row?.group_price.special_price > 0 &&
                  params?.row?.group_price.special_price !== null &&
                  params?.row?.group_price.special_price !== undefined
                ? params?.row?.group_price.special_price
                : params?.row?.group_price?.price
            )}`}
          </Typography>
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 2,
      sortable: false,
      headerAlign: "center",
      align: "center",
      userRoles: [1],
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            component="p"
            sx={{
              "&.MuiTypography-root": {
                fontSize: font.size.sm,
              },
            }}
          >
            {params?.row?.product?.status}
          </Typography>
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Del.",
      flex: 1,
      sortable: false,
      headerAlign: "center",
      align: "center",
      userRoles: [1],
      renderCell: (params) => (
        <IconButton
          sx={{
            color: Black,
            backgroundColor: hexToRgba(White01, 0.4),
          }}
          onClick={() => {
            openDialog({
              type: "confirm",
              title: "คุณต้องการลบใช่หรือไม่?",
              onConfirm: () => {
                let index = params.api.getRowIndex(params.row._id);
                removeElement(index);
                closeDialog();
              },
              maxWidth: 342,
            });
          }}
        >
          <DeleteOutlineRoundedIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <Main open={open}>
        <Container>
          <BoxWrapper>
            <LabelSection
              title="ชื่อ/ประเภทโค้ดส่วนลด"
              style={{ margin: 0 }}
              headerStyle={{ marginBottom: 24 }}
              titleStyle={{ fontSize: font.size.lg }}
            >
              <Grid container spacing={3} rowSpacing={3} columnSpacing={5}>
                <Grid item xs={12} md={6}>
                  <SpanLabel>
                    ชื่อโค้ดส่วนลด<span>*</span>
                  </SpanLabel>
                  <TextField
                    placeholder="ชื่อโค้ดส่วนลด"
                    variant="outlined"
                    fullWidth
                    value={formData?.promo_name}
                    onChange={(e) => handleChange(e, `promo_name`)}
                    error={!!formErrors[`promo_name`]}
                    helperText={
                      !!formErrors[`promo_name`]
                        ? "Please enter promo name"
                        : ""
                    }
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <SpanLabel>
                    รหัสโค้ดส่วนลด<span>*</span>
                  </SpanLabel>
                  <TextField
                    placeholder="รหัสโค้ดส่วนลด"
                    variant="outlined"
                    fullWidth
                    value={formData?.promo_code}
                    onChange={(e) => handleChange(e, `promo_code`)}
                    error={!!formErrors[`promo_code`]}
                    helperText={
                      !!formErrors[`promo_code`]
                        ? "Please enter promo code"
                        : ""
                    }
                  />
                </Grid>

                {/* // ? Hold */}
                {/* <Grid item xs={12}>
                  <FormControl sx={{ width: "100%" }}>
                    <FormLabel
                      id="promo-code-type-radio-group-label"
                      sx={{
                        fontSize: font.size.lg,
                        color: "var(--secondary-color)",
                        mb: 2,
                        span: {
                          color: "var(--primary-color)",
                        },
                        "&.Mui-focused": {
                          color: "var(--secondary-color)",
                        },
                      }}
                    >
                      ประเภทโค้ดส่วนลด<span>*</span>
                    </FormLabel>

                    <RadioGroup
                      row
                      aria-labelledby="promo-code-type-radio-group-label"
                      name="promo-code-type-radio-group"
                      sx={{
                        ml: -5,
                        ".MuiFormControlLabel-root": {
                          pl: 5,
                        },
                      }}
                      value={formData?.promo_type}
                      onChange={(e) => handleChange(e, `promo_type`)}
                    >
                      <FormControlLabel
                        value="product"
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label={"ส่วนลดสินค้า"}
                      />

                      <FormControlLabel
                        value="bill"
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label={"ส่วนลดท้ายบิล"}
                      />

                      <FormControlLabel
                        value="delivery"
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label={"ส่วนลดค่าขนส่ง"}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid> */}

                <Grid item xs={12}>
                  <FormControl sx={{ width: "100%" }}>
                    <FormLabel
                      id="discount-type-radio-group-label"
                      sx={{
                        fontSize: font.size.lg,
                        color: "var(--secondary-color)",
                        mb: 2,
                        span: {
                          color: "var(--primary-color)",
                        },
                        "&.Mui-focused": {
                          color: "var(--secondary-color)",
                        },
                      }}
                    >
                      การใช้ส่วนลด<span>*</span>
                    </FormLabel>

                    <RadioGroup
                      row
                      aria-labelledby="promo-condition-repeat-radio-group-label"
                      name="promo-condition-repeat-radio-group"
                      sx={{
                        ml: -5,
                        ".MuiFormControlLabel-root": {
                          pl: 5,
                        },
                      }}
                      value={formData?.promo_condition?.promo_condition_repeat}
                      onChange={(e) =>
                        handleChange(
                          e,
                          `promo_condition.promo_condition_repeat`
                        )
                      }
                    >
                      <FormControlLabel
                        value={true}
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="สามารถใช้ซ้ำได้"
                      />

                      <FormControlLabel
                        value={false}
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="ไม่สามารถใช้ซ้ำได้"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
            </LabelSection>
          </BoxWrapper>

          <BoxWrapper>
            <LabelSection
              title="เงื่อนไขโค้ดส่วนลด"
              style={{ margin: 0 }}
              headerStyle={{ marginBottom: 24 }}
              titleStyle={{ fontSize: font.size.lg }}
            >
              <Grid container spacing={3} rowSpacing={3} columnSpacing={5}>
                <Grid item xs={12}>
                  <SpanLabel style={{ margin: 0 }}>
                    เงื่อนไขการใช้ลดส่วนสินค้า/ท้ายบิล<span>*</span>
                  </SpanLabel>
                </Grid>

                <Grid item xs={12}>
                  <FormControl sx={{ width: "100%" }}>
                    <FormLabel
                      id="promo-condition-radio-group-label"
                      sx={{
                        fontSize: font.size.md,
                        color: "var(--secondary-color)",
                        mb: 2,
                        span: {
                          color: "var(--primary-color)",
                        },
                        "&.Mui-focused": {
                          color: "var(--secondary-color)",
                        },
                      }}
                    >
                      กลุ่มลูกค้า
                    </FormLabel>

                    <RadioGroup
                      row
                      aria-labelledby="promo-condition-radio-group-label"
                      name="promo-condition-radio-group"
                      sx={{
                        ml: -5,
                        ".MuiFormControlLabel-root": {
                          m: 0,
                          flex: "0 0 50%",
                          width: "50%",
                          pl: 5,
                        },
                      }}
                      value={formData?.promo_condition?.promo_condition_user}
                      onChange={(e) =>
                        handleChange(e, `promo_condition.promo_condition_user`)
                      }
                    >
                      <FormControlLabel
                        value="new"
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="ลูกค้าใหม่"
                      />

                      <FormControlLabel
                        value="repeat"
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="ลูกค้าซื้อซ้ำ"
                      />

                      <FormControlLabel
                        value="all"
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="ลูกค้าทุกคน"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl sx={{ width: "100%" }}>
                    <FormLabel
                      id="promo-product-group-radio-group-label"
                      sx={{
                        fontSize: font.size.md,
                        color: "var(--secondary-color)",
                        mb: 2,
                        span: {
                          color: "var(--primary-color)",
                        },
                        "&.Mui-focused": {
                          color: "var(--secondary-color)",
                        },
                      }}
                    >
                      กลุ่มสินค้า
                    </FormLabel>

                    <RadioGroup
                      row
                      aria-labelledby="promo-product-group-radio-group-label"
                      name="promo-product-group-radio-group"
                      sx={{
                        ml: -5,
                        ".MuiFormControlLabel-root": {
                          m: 0,
                          flex: "0 0 50%",
                          width: "50%",
                          pl: 5,
                        },
                      }}
                      value={formData?.promo_product_group}
                      onChange={(e) => handleChange(e, `promo_product_group`)}
                    >
                      <FormControlLabel
                        value="ready"
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="สินค้าพร้อมส่ง"
                      />

                      <FormControlLabel
                        value="pre-order"
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="สินค้าพรีออเดอร์"
                      />

                      <FormControlLabel
                        value="all"
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="สินค้าทุกกลุ่ม"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl sx={{ width: "100%" }}>
                    <FormLabel
                      id="promo-pay-type-radio-group-label"
                      sx={{
                        fontSize: font.size.md,
                        color: "var(--secondary-color)",
                        mb: 2,
                        span: {
                          color: "var(--primary-color)",
                        },
                        "&.Mui-focused": {
                          color: "var(--secondary-color)",
                        },
                      }}
                    >
                      วิธีการชำระเงิน
                    </FormLabel>

                    <RadioGroup
                      row
                      aria-labelledby="promo-product-group-radio-group-label"
                      name="promo-product-group-radio-group"
                      sx={{
                        ml: -5,
                        ".MuiFormControlLabel-root": {
                          m: 0,
                          flex: "0 0 50%",
                          width: "50%",
                          pl: 5,
                        },
                      }}
                      value={formData?.promo_pay_type}
                      onChange={(e) => handleChange(e, `promo_pay_type`)}
                    >
                      <FormControlLabel
                        value="promptpay"
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="ชำระผ่านพร้อมเพย์/โอนผ่านบัญชีธนาคาร"
                      />

                      <FormControlLabel
                        value="credit-card"
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="ชำระผ่านบัตรเครดิต"
                      />

                      {/* <FormControlLabel
                        value="installment"
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="ผ่อนชำระ"
                      /> */}

                      <FormControlLabel
                        value="all"
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="ทุกการชำระเงิน"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
            </LabelSection>
          </BoxWrapper>

          <BoxWrapper>
            <LabelSection
              title="รายละเอียดโค้ดส่วนลด"
              style={{ margin: 0 }}
              headerStyle={{ marginBottom: 24 }}
              titleStyle={{ fontSize: font.size.lg }}
            >
              <Grid container spacing={3} rowSpacing={3} columnSpacing={5}>
                <Grid item xs={12}>
                  <FormControl sx={{ width: "100%" }}>
                    <FormLabel
                      id="discount-type-radio-group-label"
                      sx={{
                        fontSize: font.size.lg,
                        color: "var(--secondary-color)",
                        mb: 2,
                        span: {
                          color: "var(--primary-color)",
                        },
                        "&.Mui-focused": {
                          color: "var(--secondary-color)",
                        },
                      }}
                    >
                      ประเภทของส่วนลด<span>*</span>
                    </FormLabel>

                    <RadioGroup
                      row
                      aria-labelledby="discount-type-radio-group-label"
                      name="discount-type-radio-group"
                      sx={{
                        ml: -5,
                        // gap: 1,
                        ".MuiFormControlLabel-root": {
                          pl: 5,
                          zIndex: 2,
                        },
                      }}
                      value={formData?.promo_discount?.promo_discount_type}
                      onChange={(e) =>
                        handleChange(e, `promo_discount.promo_discount_type`)
                      }
                    >
                      <FormControlLabel
                        value="amount"
                        sx={{
                          flex: "1 1 100%",
                          width: "100%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="ลดเป็นจำนวนเงิน / บาท"
                      />

                      <Grid
                        container
                        spacing={3}
                        rowSpacing={3}
                        columnSpacing={2}
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          pl: 6,
                          py: 1,
                          transition: "height 0.3s ease-in-out",
                          overflow: "hidden",
                          ...(formData.promo_discount.promo_discount_type ===
                          "amount"
                            ? {
                                height: "105px",
                              }
                            : {
                                height: 0,
                              }),
                        }}
                      >
                        <Grid item xs={6}>
                          <TextField
                            label="มูลค่าส่วนลด / บาท"
                            variant="outlined"
                            fullWidth
                            size="small"
                            sx={{
                              ".MuiTextField-root": {
                                position: "relative",
                              },
                              ".MuiFormHelperText-root": {
                                position: "absolute",
                                bottom: "-20px",
                                width: "max-content",
                              },
                            }}
                            value={
                              formData?.promo_discount?.promo_discount_value ||
                              ""
                            }
                            onChange={(e) => {
                              if (e.target.value * 0 === 0)
                                handleChange(
                                  null,
                                  `promo_discount.promo_discount_value`,
                                  e.target.value
                                );
                            }}
                            onBlur={(e) => {
                              if (e.target.value * 0 === 0)
                                handleChange(
                                  null,
                                  `promo_discount.promo_discount_value`,
                                  parseFloat(e.target.value) || null
                                );
                            }}
                            error={
                              !!formErrors[
                                `promo_discount.promo_discount_value`
                              ]
                            }
                            helperText={
                              !!formErrors[
                                `promo_discount.promo_discount_value`
                              ]
                                ? formErrors[
                                    `promo_discount.promo_discount_value`
                                  ].message
                                : ""
                            }
                          />
                        </Grid>

                        <Grid item xs={6}>
                          <TextField
                            label="ขั้นต่ำในการสั่งซื้อ"
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={
                              formData?.promo_discount
                                .promo_discount_minimum_spend === 0
                                ? `${formData?.promo_discount.promo_discount_minimum_spend}`
                                : !!formData?.promo_discount
                                    .promo_discount_minimum_spend
                                ? formData?.promo_discount
                                    .promo_discount_minimum_spend
                                : ""
                            }
                            onChange={(e) => {
                              if (e.target.value * 0 === 0)
                                handleChange(
                                  null,
                                  `promo_discount.promo_discount_minimum_spend`,
                                  e.target.value
                                );
                            }}
                            onBlur={(e) => {
                              if (e.target.value * 0 === 0)
                                handleChange(
                                  null,
                                  `promo_discount.promo_discount_minimum_spend`,
                                  !!e.target.value || e.target.value === 0
                                    ? parseFloat(e.target.value)
                                    : null
                                );
                            }}
                            error={
                              !!formErrors[
                                `promo_discount.promo_discount_minimum_spend`
                              ]
                            }
                            // helperText={
                            //   !!formErrors[`promo_discount.promo_discount_minimum_spend`]
                            //     ? "Please enter discount minimum spend"
                            //     : ""
                            // }
                          />
                        </Grid>
                      </Grid>

                      <FormControlLabel
                        value="percent"
                        sx={{
                          flex: "1 1 100%",
                          width: "100%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="ลดเป็นเปอร์เซ็น %"
                      />

                      <Grid
                        container
                        spacing={3}
                        rowSpacing={3}
                        columnSpacing={2}
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          pl: 6,
                          py: 1,
                          transition: "height 0.3s ease-in-out",
                          overflow: "hidden",
                          ...(formData.promo_discount.promo_discount_type ===
                          "percent"
                            ? {
                                height: "150px",
                              }
                            : {
                                height: 0,
                              }),
                        }}
                      >
                        <Grid item xs={6}>
                          <TextField
                            label="มูลค่าส่วนลด / %"
                            variant="outlined"
                            fullWidth
                            size="small"
                            sx={{
                              ".MuiTextField-root": {
                                position: "relative",
                              },
                              ".MuiFormHelperText-root": {
                                position: "absolute",
                                bottom: "-20px",
                                width: "max-content",
                              },
                            }}
                            value={
                              formData?.promo_discount
                                ?.promo_discount_percent || ""
                            }
                            onChange={(e) => {
                              if (e.target.value * 0 === 0)
                                handleChange(
                                  null,
                                  `promo_discount.promo_discount_percent`,
                                  e.target.value
                                );
                            }}
                            onBlur={(e) => {
                              if (e.target.value * 0 === 0)
                                handleChange(
                                  null,
                                  `promo_discount.promo_discount_percent`,
                                  parseFloat(e.target.value) || 0
                                );
                            }}
                            error={
                              !!formErrors[
                                `promo_discount.promo_discount_percent`
                              ]
                            }
                            helperText={
                              !!formErrors[
                                `promo_discount.promo_discount_percent`
                              ]
                                ? // ? "Please enter discount percent"
                                  formErrors[
                                    `promo_discount.promo_discount_percent`
                                  ].message
                                : ""
                            }
                          />
                        </Grid>

                        <Grid item xs={6}>
                          <TextField
                            label="ขั้นต่ำในการสั่งซื้อ"
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={
                              formData?.promo_discount
                                .promo_discount_minimum_spend === 0
                                ? `${formData?.promo_discount.promo_discount_minimum_spend}`
                                : !!formData?.promo_discount
                                    .promo_discount_minimum_spend
                                ? formData?.promo_discount
                                    .promo_discount_minimum_spend
                                : ""
                            }
                            onChange={(e) => {
                              if (e.target.value * 0 === 0)
                                handleChange(
                                  null,
                                  `promo_discount.promo_discount_minimum_spend`,
                                  e.target.value
                                );
                            }}
                            onBlur={(e) => {
                              if (e.target.value * 0 === 0)
                                handleChange(
                                  null,
                                  `promo_discount.promo_discount_minimum_spend`,
                                  !!e.target.value || e.target.value === 0
                                    ? parseFloat(e.target.value)
                                    : null
                                );
                            }}
                            error={
                              !!formErrors[
                                `promo_discount.promo_discount_minimum_spend`
                              ]
                            }
                            // helperText={
                            //   !!formErrors[`promo_discount.promo_discount_minimum_spend`]
                            //     ? "Please enter minimum spend"
                            //     : ""
                            // }
                          />
                        </Grid>

                        <Grid item xs={6}>
                          <TextField
                            label="มูลค่าส่วนลดสูงสุด / บาท"
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={
                              formData?.promo_discount.promo_discount_maximum ||
                              ""
                            }
                            onChange={(e) => {
                              if (e.target.value * 0 === 0)
                                handleChange(
                                  null,
                                  `promo_discount.promo_discount_maximum`,
                                  e.target.value
                                );
                            }}
                            onBlur={(e) => {
                              if (e.target.value * 0 === 0)
                                handleChange(
                                  null,
                                  `promo_discount.promo_discount_maximum`,
                                  parseFloat(e.target.value) || 0
                                );
                            }}
                            error={
                              !!formErrors[
                                `promo_discount.promo_discount_maximum`
                              ]
                            }
                            // helperText={
                            //   !!formErrors[`promo_discount.promo_discount_maximum`]
                            //     ? "Please enter discount maximum"
                            //     : ""
                            // }
                          />
                        </Grid>
                      </Grid>
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <SpanLabel>
                    ระยะเวลาของส่วนลด<span>*</span>
                  </SpanLabel>

                  <Grid
                    container
                    spacing={3}
                    rowSpacing={3}
                    columnSpacing={2}
                    sx={{
                      flex: "0 0 50%",
                      width: "50%",
                    }}
                  >
                    <Grid item xs={6}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Start date"
                          inputFormat="DD/MM/YYYY"
                          value={formData?.promo_start_time}
                          onChange={(newValue) =>
                            handleChange(null, `promo_start_time`, newValue)
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              fullWidth
                              size="small"
                              sx={{
                                // my: 2,
                                width: "100%",
                                background: "#fff",
                                ".MuiOutlinedInput-root": {
                                  paddingRight: "18px",
                                },
                              }}
                              error={!!formErrors[`promo_start_time`]}
                              helperText={
                                !!formErrors[`promo_start_time`]
                                  ? formErrors[`promo_start_time`].message
                                  : ""
                              }
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>

                    <Grid item xs={6}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Expiry date"
                          inputFormat="DD/MM/YYYY"
                          value={formData?.promo_expire_time}
                          onChange={(newValue) =>
                            handleChange(null, `promo_expire_time`, newValue)
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              fullWidth
                              size="small"
                              sx={{
                                // my: 2,
                                width: "100%",
                                background: "#fff",
                                ".MuiOutlinedInput-root": {
                                  paddingRight: "18px",
                                },
                              }}
                              error={!!formErrors[`promo_expire_time`]}
                              helperText={
                                !!formErrors[`promo_expire_time`]
                                  ? formErrors[`promo_expire_time`].message
                                  : ""
                              }
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        label="ปริมาณของส่วนลด"
                        variant="outlined"
                        fullWidth
                        size="small"
                        disabled={isUnlimit}
                        value={formData?.amount || ""}
                        onChange={(e) => {
                          if (e.target.value * 0 === 0)
                            handleChange(null, `amount`, e.target.value);
                        }}
                        onBlur={(e) => {
                          if (e.target.value * 0 === 0)
                            handleChange(
                              null,
                              `amount`,
                              parseFloat(e.target.value) || 0
                            );
                        }}
                        error={!!formErrors[`amount`]}
                        // helperText={
                        //   !!formErrors[`amount`]
                        //     ? "Please enter promo code amount"
                        //     : ""
                        // }
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <FormControlLabel
                        label="ไม่จำกัด"
                        sx={{
                          width: "100%",
                          maxHeight: "72px",
                          margin: 0,
                        }}
                        control={
                          <Checkbox
                            name="unlimit-promo-code-amount-checkbox"
                            sx={{
                              "&.Mui-checked": {
                                color: "#e04132",
                              },
                            }}
                            checked={isUnlimit}
                            onChange={(e) => {
                              handleChange(
                                null,
                                `amount.is_unlimit`,
                                e.target.checked
                              );
                              setIsUnlimit(e.target.checked);
                            }}
                          />
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <FormControl sx={{ width: "100%" }}>
                    <FormLabel
                      id="responsible-by-radio-group-label"
                      sx={{
                        fontSize: font.size.md,
                        color: "var(--secondary-color)",
                        mb: 2,
                        span: {
                          color: "var(--primary-color)",
                        },
                        "&.Mui-focused": {
                          color: "var(--secondary-color)",
                        },
                      }}
                    >
                      ผู้รับผิดชอบค่าใช้จ่ายของส่วนลด<span>*</span>
                    </FormLabel>

                    <RadioGroup
                      row
                      aria-labelledby="responsible-by-radio-group-label"
                      name="responsible-by-radio-group"
                      sx={{
                        ml: -5,
                        ".MuiFormControlLabel-root": {
                          pl: 5,
                          zIndex: 2,
                        },
                      }}
                      value={formData?.promo_responsible?.promo_responsible_by}
                      onChange={(e) =>
                        handleChange(
                          e,
                          `promo_responsible.promo_responsible_by`
                        )
                      }
                    >
                      <FormControlLabel
                        value="xsurface"
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="Xsurface"
                      />

                      <FormControlLabel
                        value="seller"
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="ร้านค้า"
                      />

                      <FormControlLabel
                        value="half"
                        sx={{
                          flex: "1 1 100%",
                          width: "100%",
                          m: 0,
                          "&.MuiRadio-root": {
                            width: "auto",
                          },
                        }}
                        control={
                          <Radio
                            sx={{
                              fontSize: font.size.md,
                              color: "var(--primary-color)",
                              "&.Mui-checked": {
                                color: "var(--primary-color)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 22,
                              },
                            }}
                          />
                        }
                        label="รับผิดชอบร่วมกัน"
                      />

                      <Grid
                        container
                        spacing={3}
                        rowSpacing={3}
                        columnSpacing={2}
                        sx={{
                          flex: "0 0 50%",
                          width: "50%",
                          pl: 6,
                          py: 1,
                          transition: "height 0.3s ease-in-out",
                          overflow: "hidden",
                          ...(formData.promo_responsible
                            .promo_responsible_by === "half"
                            ? {
                                height: "85px",
                              }
                            : {
                                height: 0,
                              }),
                        }}
                      >
                        <Grid item xs={6}>
                          <TextField
                            label="บริษัทรับผิดชอบ / %"
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={
                              formData?.promo_responsible
                                ?.promo_xsurface_percent || ""
                            }
                            onChange={(e) => {
                              if (e.target.value * 0 === 0)
                                handleChange(
                                  null,
                                  `promo_responsible.promo_xsurface_percent`,
                                  e.target.value
                                );
                            }}
                            onBlur={(e) => {
                              if (e.target.value * 0 === 0)
                                handleChange(
                                  null,
                                  `promo_responsible.promo_xsurface_percent`,
                                  parseFloat(e.target.value) || 0
                                );
                            }}
                            error={
                              !!formErrors[
                                `promo_responsible.promo_xsurface_percent`
                              ]
                            }
                            // helperText={
                            //   !!formErrors[`promo_responsible.promo_xsurface_percent`]
                            //     ? "Please enter percent"
                            //     : ""
                            // }
                          />
                        </Grid>

                        <Grid item xs={6}>
                          <TextField
                            label="ร้านค้ารับผิดชอบ / %"
                            variant="outlined"
                            fullWidth
                            size="small"
                            value={
                              formData?.promo_responsible
                                ?.promo_seller_percent || ""
                            }
                            onChange={(e) => {
                              if (e.target.value * 0 === 0)
                                handleChange(
                                  null,
                                  `promo_responsible.promo_seller_percent`,
                                  e.target.value
                                );
                            }}
                            onBlur={(e) => {
                              if (e.target.value * 0 === 0)
                                handleChange(
                                  null,
                                  `promo_responsible.promo_seller_percent`,
                                  parseFloat(e.target.value) || 0
                                );
                            }}
                            error={
                              !!formErrors[
                                `promo_responsible.promo_seller_percent`
                              ]
                            }
                            // helperText={
                            //   !!formErrors[`promo_responsible.promo_seller_percent`]
                            //     ? "Please enter promo name"
                            //     : ""
                            // }
                          />
                        </Grid>
                      </Grid>
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <SpanLabel>
                    รายละเอียดการใช้ส่วนลด<span>*</span>
                  </SpanLabel>
                  <TextField
                    placeholder="รายละเอียดที่แสดงบนหน้าการ์ดส่วนลด"
                    variant="outlined"
                    fullWidth
                    inputProps={{ maxLength: 50 }}
                    value={formData?.promo_title}
                    onChange={(e) => handleChange(e, `promo_title`)}
                    error={!!formErrors[`promo_title`]}
                    helperText={
                      !!formErrors[`promo_title`] ? (
                        "กรุณากรอกรายละเอียดการใช้ส่วนลด"
                      ) : (
                        <Box
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          {formData?.promo_title?.length}/50
                        </Box>
                      )
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextEditor
                    name={`promo_description`}
                    value={formData?.promo_description}
                    onChange={(data) => {
                      handleChange(null, `promo_description`, data);
                    }}
                    editorLoaded={editorLoaded}
                    customConfig={{
                      toolbar: ["undo", "redo", "|", "bulletedList"],
                      heading: {
                        options: [
                          {
                            model: "paragraph",
                            title: "Body",
                            class: "ck-heading_paragraph",
                          },
                        ],
                      },
                    }}
                  />
                  <Stack
                    flexDirection="row"
                    justifyContent="space-between"
                    sx={{ mt: 2 }}
                  >
                    <Button
                      variant="text"
                      sx={{
                        textDecoration: "underline",
                        py: 0,
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                      onClick={() => {
                        openDialog({
                          type: "custom",
                          customDialog: (
                            <SuggestConditionDialog
                              descData={formData?.promo_description}
                              onClose={() => closeDialog()}
                              onSubmit={(value) => {
                                handleChange(null, `promo_description`, value);
                                closeAllDialogs();
                              }}
                            />
                          ),
                        });
                      }}
                    >
                      เงื่อนไขแนะนำ
                    </Button>
                    {formErrors[`promo_description`] && (
                      <FormHelperText
                        sx={{ width: "max-content", m: 0 }}
                        error={!!formErrors[`promo_description`]}
                      >
                        กรุณาระบุเงื่อนไขการใช้ส่วนลด
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </LabelSection>
          </BoxWrapper>

          <BoxWrapper>
            <LabelSection
              title="เลือกสินค้าสำหรับการใช้ส่วนลด"
              style={{ margin: 0 }}
              headerStyle={{ marginBottom: 16 }}
              titleStyle={{ fontSize: font.size.lg }}
              actionMenu={() => (
                <Button
                  startIcon={<AddIcon />}
                  variant="secondary"
                  size="large"
                  sx={{
                    minWidth: 160,
                  }}
                  onClick={() => {
                    openDialog({
                      type: "custom",
                      customDialog: (
                        <AddProductDialog
                          onClose={() => closeDialog()}
                          onAppendData={(value) => {
                            handleChange(
                              null,
                              `product_choice_promocodes`,
                              value
                            );
                            closeAllDialogs();
                          }}
                          selectedData={formData?.product_choice_promocodes}
                          productGroup={formData?.promo_product_group}
                        />
                      ),
                    });
                  }}
                >
                  เพิ่มสินค้า
                </Button>
              )}
            >
              {formData?.product_choice_promocodes?.length > 0 && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          mr: 1,
                          color: DarkGrey,
                        }}
                        component="span"
                      >
                        สินค้าที่เลือก :
                      </Typography>
                      <Typography
                        sx={{
                          color: Primary,
                        }}
                        component="span"
                      >
                        {formData?.product_choice_promocodes?.length}
                      </Typography>
                    </Box>

                    <Button
                      startIcon={<DeleteOutlineIcon />}
                      variant="text"
                      size="large"
                      sx={{
                        minWidth: 160,
                        "&.MuiButton-root.MuiButton-text": {
                          color: Black,
                        },
                      }}
                      onClick={() => {
                        openDialog({
                          type: "confirm",
                          title: "คุณต้องการลบสินค้าที่เลือกทั้งหมดใช่หรือไม่?",
                          onConfirm: () => {
                            handleChange(null, `product_choice_promocodes`, []);
                            closeDialog();
                          },
                          maxWidth: 350,
                        });
                      }}
                    >
                      ลบทั้งหมด
                    </Button>
                  </Box>

                  <WrapperDataGrid>
                    <DataGrid
                      rows={formData?.product_choice_promocodes}
                      getRowId={(row) => row._id}
                      getRowHeight={() => "auto"}
                      columns={columns}
                      pageSize={10}
                      pagination
                      components={{
                        NoRowsOverlay,
                        NoResultsOverlay,
                      }}
                      componentsProps={{
                        basePopper: {},
                      }}
                      hideFooterSelectedRowCount
                      disableSelectionOnClick
                      disableColumnMenu
                    />
                  </WrapperDataGrid>
                </>
              )}
            </LabelSection>
          </BoxWrapper>

          <BoxWrapper>
            <LabelSection
              title="บันทึก"
              style={{ margin: 0 }}
              headerStyle={{ marginBottom: 24 }}
              titleStyle={{ fontSize: font.size.lg }}
            >
              <TextField
                id="note-input"
                fullWidth
                multiline
                rows={6}
                onChange={(e) => handleChange(e, `note`)}
                value={formData.note}
                placeholder="ใช้สำหรับการจดบันทึกสำหรับภายใน (ลูกค้าไม่เห็น)"
              />
            </LabelSection>
          </BoxWrapper>

          <ButtonGroup
            flexDirection={"row"}
            justifyContent={"flex-end"}
            alignItems={"center"}
            columnGap={2}
          >
            <Button
              variant="outlined"
              size="large"
              onClick={() => {
                openDialog({
                  type: "confirm",
                  title: "คุณต้องการยกเลิกการสร้างโค้ดใช่หรือไม่?",
                  onConfirm: () => {
                    window.history.back();
                    closeDialog();
                  },
                  maxWidth: 342,
                });
              }}
              sx={{
                width: "100%",
                maxWidth: 140,
                borderRadius: 4,
              }}
            >
              ยกเลิก
            </Button>

            <Button
              variant="contained"
              size="large"
              disabled={!isValid}
              onClick={() => {
                openDialog({
                  type: "custom",
                  customDialog: (
                    <PreviewDialog
                      previewData={formData}
                      onClose={() => closeDialog()}
                      onSuccess={() => {
                        closeAllDialogs();
                        setTimeout(() => {
                          navigate(`/promo-code/list`);
                        }, 1500);
                      }}
                    />
                  ),
                });
              }}
              sx={{ width: "100%", maxWidth: 140, borderRadius: 4 }}
            >
              บันทึก
            </Button>
          </ButtonGroup>
        </Container>
      </Main>

      {isLoading && <Loading />}
    </>
  );
};

export default CreatePromoCode;
